import { AlertColor, Box, Container, Grid, Typography, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import ConfirmModal from 'components/ConfirmModal';
import Dropdown from 'components/Dropdown';
import PageLoader from 'components/PageLoader';
import { FC, useCallback, useState } from 'react';
import Notification from '../../../components/Notification';
import axiosInstance from '../../../utils/axios';
import { URLS } from 'utils/constants/urls';

interface DeleteProps {
  openModal?: boolean;
  closeModal: any;
  deleteUrl: string;
  updateList: () => void;
  page?: string;
  entityName?: string;
}

const Delete: FC<DeleteProps> = ({ openModal, closeModal, deleteUrl, updateList, page, entityName }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [assignTask, setAssignTask] = useState<boolean>(false);
  const [usersDropdown, setUsersDropdown] = useState<[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const handleError = (error: any) => {
    if (error.response) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response.data?.error_description ?? error.response.data.message,
      });
    } else {
      setMessage({
        display: true,
        severity: 'error',
        message: error.message,
      });
    }
  };

  const resetAssignState = useCallback(() => {
    setAssignTask(false);
    setSelectedUser('');
    setUsersDropdown([]);
  }, []);

  const handleClose = useCallback(() => {
    if (loading) return;
    resetAssignState();
    closeModal();
  }, [loading, resetAssignState, closeModal]);

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.getCompanyFilters);
      setUsersDropdown(data?.primaryManagers);
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const confirmDelete = useCallback(async () => {
    try {
      setLoading(true);
      await axiosInstance.delete(deleteUrl);
      updateList();
      resetAssignState();
      closeModal();
      setMessage({
        display: true,
        message: strings.delete_success_message,
        severity: 'success',
      });
    } catch (error: any) {
      if (error.response?.status === 400 && page === 'user') {
        setAssignTask(true);
        await fetchUser();
      } else {
        handleError(error);
      }
    } finally {
      setLoading(false);
    }
  }, [deleteUrl, updateList, closeModal, page, fetchUser, resetAssignState]);

  const handleDeleteUser = useCallback(async () => {
    if (!selectedUser) {
      setMessage({
        display: true,
        message: 'Select a user from the dropdown',
        severity: 'error',
      });
      return;
    }

    try {
      setLoading(true);
      const userId = deleteUrl.split('/').pop();
      await axiosInstance.put(URLS.assignTask(userId, selectedUser));
      await axiosInstance.delete(deleteUrl);
      updateList();
      resetAssignState();
      closeModal();
      setMessage({
        display: true,
        message: strings.delete_success_message,
        severity: 'success',
      });
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, deleteUrl, updateList, closeModal, resetAssignState]);

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  return (
    <>
      {loading && <PageLoader />}
      <Container sx={{ p: '1rem' }} maxWidth={false}>
        <ConfirmModal
          open={Boolean(openModal)}
          closeModal={handleClose}
          handleYes={assignTask ? handleDeleteUser : confirmDelete}
          title={strings.deleteText}
          subtitle={assignTask ? undefined : strings.confirmDeletedText}
          entityName={assignTask ? undefined : entityName}
          confirmLabel={strings.deleteText}
          cancelLabel={strings.cancelText}
          tone={assignTask ? 'warning' : 'danger'}
          loading={loading}
          maxWidth={assignTask ? 'sm' : 'xs'}>
          {assignTask ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55, mb: 1.5 }}>
                {strings.delete_user_warning_msg}
              </Typography>
              <Box
                width="100%"
                sx={{
                  '.MuiFormControl-root': {
                    marginLeft: 0,
                    minWidth: '100%',
                  },
                }}>
                <Dropdown
                  label="User"
                  value={selectedUser}
                  options={usersDropdown}
                  onChange={(value: any) => setSelectedUser(value)}
                  menuZIndex={theme.zIndex.modal + 3}
                />
              </Box>
            </>
          ) : null}
        </ConfirmModal>
        <Grid container spacing={2}>
          {message.display && (
            <Notification
              isOpen={message.display}
              message={message.message}
              severity={message.severity as AlertColor}
              closeNotification={closeNotification}
            />
          )}
        </Grid>
      </Container>
    </>
  );
};
export default Delete;
